import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import bnrImg1 from "../../images/banner/img1.jpg";
import waveBlue from "../../images/shap/wave-blue.png";
import circleDots from "../../images/shap/circle-dots.png";
import plusBlue from "../../images/shap/plus-blue.png";

const Graficas = () => {
  const [monto, setMonto] = useState(1000);
  const [tasa, setTasa] = useState(5);
  const [anios, setAnios] = useState(5);
  const [periodo, setPeriodo] = useState(12);

  const [simple, setSimple] = useState(0);
  const [compuesto, setCompuesto] = useState(0);
  const [dataGrafica, setDataGrafica] = useState([]);

  // Función mágica para el formato de moneda (Comas para miles, punto para decimales)
  const formatear = (valor) => {
    return Number(valor).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  useEffect(() => {
    const p = parseFloat(monto) || 0;
    const r = (parseFloat(tasa) || 0) / 100;
    const t = parseFloat(anios) || 0;
    const n = parseInt(periodo);

    const totalSimple = p * (1 + (r * t));
    const totalCompuesto = p * Math.pow((1 + (r / n)), (n * t));

    // Guardamos como números para evitar conflictos
    setSimple(totalSimple);
    setCompuesto(totalCompuesto);

    const puntos = [];
    for (let i = 0; i <= t; i++) {
      puntos.push({
        name: `Año ${i}`,
        Simple: parseFloat((p * (1 + (r * i))).toFixed(2)),
        Compuesto: parseFloat((p * Math.pow((1 + (r / n)), (n * i))).toFixed(2)),
      });
    }
    setDataGrafica(puntos);
  }, [monto, tasa, anios, periodo]);

  return (
    <div className="page-content bg-white">
      <div className="banner-wraper">
        <div className="page-banner" style={{ backgroundImage: "url(" + bnrImg1 + ")" }}>
          <div className="container">
            <div className="page-banner-entry text-center">
              <h1>SIMULADOR</h1>
              <nav aria-label="breadcrumb" className="breadcrumb-row">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/"><i className="feather-home"></i> INICIO</Link></li>
                  <li className="breadcrumb-item active">CALCULADORA INTERESES</li>
                </ul>
              </nav>
            </div>
          </div>
          <img className="pt-img1 animate-wave" src={waveBlue} alt="" />
          <img className="pt-img2 animate2" src={circleDots} alt="" />
          <img className="pt-img3 animate-rotate" src={plusBlue} alt="" />
        </div>
      </div>

      <div className="container mt-5 mb-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-11">
            <div className="card shadow border-0">
              <div className="card-header bg-dark text-white p-4 text-center">
                <h2 className="h4 mb-0 text-uppercase">Calculadora de Proyección Financiera</h2>
              </div>

              <div className="card-body p-4 bg-light">
                <div className="row g-4">
                  <div className="col-lg-4">
                    <div className="bg-white p-3 rounded border shadow-sm">
                      <label className="form-label fw-bold text-secondary">Capital Principal ($)</label>
                      <div className="input-group mb-3">
                        <span className="input-group-text">$</span>
                        <input type="number" className="form-control" value={monto} onChange={(e) => setMonto(e.target.value)} />
                      </div>

                      <label className="form-label fw-bold text-secondary">Tasa Anual: {tasa}%</label>
                      <input type="range" className="form-range mb-3" min="0" max="30" step="0.5" value={tasa} onChange={(e) => setTasa(e.target.value)} />

                      <label className="form-label fw-bold text-secondary">Tiempo (Años)</label>
                      <input type="number" className="form-control mb-3" value={anios} onChange={(e) => setAnios(e.target.value)} />

                      <label className="form-label fw-bold text-secondary">Capitalización</label>
                      <select className="form-select" value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
                        <option value="1">Anual</option>
                        <option value="4">Trimestral</option>
                        <option value="12">Mensual</option>
                        <option value="365">Diaria</option>
                      </select>
                    </div>

                    <div className="mt-4 p-3 bg-white border rounded shadow-sm border-success border-2">
                      <small className="text-muted d-block mb-1">DIFERENCIA FINAL</small>
                      {/* Cambio aplicado aquí */}
                      <h4 className="text-success fw-bold">+ ${formatear(compuesto - simple)}</h4>
                      <p className="small text-muted mb-0">El interés compuesto supera al simple gracias a la reinversión de ganancias.</p>
                    </div>
                  </div>

                  <div className="col-lg-8">
                    <div className="bg-white p-3 rounded border shadow-sm" style={{ height: '400px' }}>
                      <h6 className="text-center text-muted mb-4">Crecimiento del Capital a través del Tiempo</h6>
                      <ResponsiveContainer width="100%" height="90%">
                        <LineChart data={dataGrafica} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          {/* Cambio aplicado en el Tooltip de la gráfica */}
                          <Tooltip formatter={(value) => `$${formatear(value)}`} />
                          <Legend verticalAlign="top" height={36}/>
                          <Line type="monotone" dataKey="Simple" stroke="#007bff" strokeWidth={3} dot={false} />
                          <Line type="monotone" dataKey="Compuesto" stroke="#28a745" strokeWidth={3} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    
                    <div className="row mt-3">
                        <div className="col-6">
                            <div className="p-2 border-start border-primary border-4 bg-white shadow-sm">
                                <small className="text-muted">TOTAL SIMPLE</small>
                                {/* Cambio aplicado aquí */}
                                <h5 className="mb-0">${formatear(simple)}</h5>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="p-2 border-start border-success border-4 bg-white shadow-sm">
                                <small className="text-muted">TOTAL COMPUESTO</small>
                                {/* Cambio aplicado aquí */}
                                <h5 className="mb-0">${formatear(compuesto)}</h5>
                            </div>
                        </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-footer text-center py-3 bg-white">
                <small className="text-muted">Derechos Reservados Altheca Development 2026</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Graficas;